import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { WaitlistForm } from "./WaitlistForm";
import { NomoexCard3D } from "./NomoexCard3D";
import { EASE } from "./Reveal";

const lines = [
  { t: "One balance." },
  { t: "One card." },
  { t: "Every chain.", accent: true },
];

const Line = ({ text, i, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${accent ? "text-[#3B5FE8]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

const chip = "rounded-full border border-slate-200 bg-white/80 px-3.5 py-1.5 font-mono text-[11px] text-slate-500 backdrop-blur-sm"; // eslint-disable-line no-unused-vars

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yGrid = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} id="top" className="noise-overlay relative overflow-hidden bg-[#FAFAFB] pt-28 pb-20 sm:pt-36 sm:pb-24 lg:pt-44 lg:pb-36">
      <motion.div style={{ y: yGrid }} className="hero-grid-bg absolute inset-0" aria-hidden="true" />
      <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-[#3B5FE8]/10 blur-[130px]" aria-hidden="true" />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-20 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="overline mb-7 flex items-center gap-2"
            data-testid="hero-overline"
          >
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-[#3B5FE8]" />
            The future of money
          </motion.p>
          <h1 data-testid="hero-headline" className="font-heading text-5xl font-medium leading-[1.02] tracking-tighter text-[#0A0C14] sm:text-6xl lg:text-[5.2rem]">
            {lines.map((l, i) => (
              <Line key={i} text={l.t} i={i} accent={l.accent} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 lg:text-lg"
            data-testid="hero-subtext"
          >
            Spend your digital dollars anywhere. Accept, pay out and send money across 7+ chains — one card, one ledger.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10"
          >
            <WaitlistForm source="hero" />
            <p className="mt-5 text-xs tracking-wide text-slate-400" data-testid="hero-caption">Early access · USDT &amp; USDC · 7+ chains · Instant P2P</p>
          </motion.div>
        </div>

        <motion.div style={{ y: yCard }} className="relative mt-12 lg:col-span-6 lg:mt-0">
          <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
            <div className="h-[300px] w-[300px] rounded-full border border-[#3B5FE8]/10 sm:h-[460px] sm:w-[460px]" />
            <div className="absolute h-[210px] w-[210px] rounded-full border border-[#3B5FE8]/20 sm:h-[330px] sm:w-[330px]" />
            <div className="absolute h-64 w-64 rounded-full bg-[#3B5FE8]/15 blur-[100px]" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 80, rotate: -6 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ duration: 1.3, delay: 0.6, ease: EASE }}
            className="relative z-10"
          >
            <NomoexCard3D className="mx-auto w-full max-w-[300px] sm:max-w-md" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="float-slower absolute -top-4 right-0 z-20 hidden rounded-2xl border border-slate-200 bg-white/90 px-5 py-3.5 shadow-[0_16px_40px_rgba(10,12,20,0.1)] backdrop-blur-sm sm:block lg:-right-2"
            data-testid="hero-badge-balance"
          >
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Card balance</p>
            <p className="font-mono mt-0.5 text-sm text-slate-800"><span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 align-middle" />12,480.22 USDC</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: EASE }}
            className="float-slow absolute -bottom-14 left-0 z-20 hidden rounded-2xl bg-[#0A0C14] px-5 py-3.5 text-white shadow-[0_20px_44px_rgba(10,12,20,0.35)] sm:block lg:-left-2"
            data-testid="hero-badge-p2p"
          >
            <p className="text-[10px] uppercase tracking-widest text-white/40">P2P transfer</p>
            <p className="font-mono mt-0.5 text-sm">@lena → @marco · 250 USDT · <span className="text-emerald-400">instant</span></p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5, ease: EASE }}
            className="absolute -bottom-14 right-0 z-20 hidden rounded-2xl bg-[#3B5FE8] px-5 py-3.5 text-white shadow-[0_20px_40px_rgba(59,95,232,0.35)] lg:block"
            data-testid="hero-badge-checkout"
          >
            <p className="text-[10px] uppercase tracking-widest text-white/60">Checkout paid</p>
            <p className="font-mono mt-0.5 text-sm">$1,250.00 · Base · ~4s</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
